import React, { useEffect, useState } from 'react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import useSWR from 'swr';
import { ascending } from 'd3';
import { TightLayout } from '@/components/layout/Layout';
import { IAreaGraph } from '@/types/explorer';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/shadcn/table';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/shadcn/card';
import { getLatestTrainerReviews } from '@/api/qa';
import { ITrainerReview } from '@/types/trainers';
import { initCapitalize, parseCamelCase } from '@/utils/strings';
import useExplorerData from '@/hooks/useExplorerData';
import AreaChart from '@/components/ui/charts/AreaChart';
import { monthDayTime } from '@/utils/dates';
import Contribute from '@/components/ui/shared/Contribute';
import CustomTooltip from '@/components/ui/charts/CustomTooltip';

const REFRESH_INTERVAL = 180000; // 3 minutes

const columns: ColumnDef<ITrainerReview>[] = [
  {
    accessorKey: 'type',
    header: () => 'Trainer',
    cell: ({ row }) => (
      <div className="text-xs md:text-sm">
        {initCapitalize(row.getValue('type'))}
      </div>
    ),
  },
  {
    accessorKey: 'location',
    header: () => 'Location',
    cell: ({ row }) => (
      <div className="text-xs uppercase md:text-sm">
        {row.getValue('location')}
      </div>
    ),
  },
  {
    accessorKey: 'category',
    header: () => 'Review Type',
    cell: ({ row }) => (
      <div className="text-xs md:text-sm">
        {initCapitalize(parseCamelCase(row.getValue('category')))}
      </div>
    ),
  },
  {
    accessorKey: 'updatedAt',
    header: () => 'Review Date',
    cell: ({ row }) => (
      <div className="text-xs md:text-sm">
        {monthDayTime(row.getValue('updatedAt'))}
      </div>
    ),
  },
];

export default function TrainersReviews() {
  const { data: explorerData } = useExplorerData();
  const [data, setData] = useState<ITrainerReview[]>([]);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [pageIndex] = React.useState(0);

  data.sort((a, b) => (a.updatedAt > b.updatedAt ? -1 : 1));

  let graphData: IAreaGraph[] = [];

  const stats = explorerData?.stats;

  const total =
    stats?.byDay?.[stats?.mostRecentDay]?.totalAiTrainerReviews || 0;
  if (stats) {
    graphData = Object.entries(stats?.byDay)
      .map(([date, value]) => ({
        date,
        value: value.totalAiTrainerReviews,
      }))
      .sort((a, b) => ascending(a.date, b.date));
  }

  const { data: updatedData, error } = useSWR(
    '/ai-trainer-reviews',
    getLatestTrainerReviews,
    {
      refreshInterval: REFRESH_INTERVAL,
    },
  );

  useEffect(() => {
    if (updatedData && !error) {
      setData(updatedData);
      setIsDataLoaded(true);
    }
  }, [updatedData, error]);

  const table = useReactTable({
    data: data,
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: {
      pagination: {
        pageIndex,
        pageSize: 100,
      },
    },
  });

  return (
    <TightLayout>
      <div className="flex h-full flex-col lg:flex-row">
        <div className="flex h-1/2 w-full flex-col gap-2 px-4 py-2 md:gap-4 md:p-4 lg:h-full lg:w-1/2">
          <div className="no-scrollbar h-full flex-1 overflow-scroll rounded-md border [&>div]:overflow-visible">
            <Table className="table-fixed">
              <TableHeader className="sticky top-0 bg-neutral-800">
                {table.getHeaderGroups().map(headerGroup => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header, index) => {
                      return (
                        <TableHead
                          key={header.id}
                          data-order={index}
                          className="h-4 w-1/4 p-2 data-[order='0']:w-1/3 data-[order='1']:w-1/5 data-[order='2']:w-1/6 data-[order='3']:w-1/4 md:h-12 md:px-4 md:data-[order='0']:w-1/4 md:data-[order='1']:w-1/5 md:data-[order='2']:w-1/4 md:data-[order='3']:w-1/4">
                          <div className="flex items-center gap-1 px-0 text-[10px] leading-none md:text-sm">
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                          </div>
                        </TableHead>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {isDataLoaded ? (
                  table.getRowModel().rows?.length ? (
                    table.getRowModel().rows.map(row => (
                      <TableRow
                        key={row.id}
                        className="leading-none hover:bg-neutral-900"
                        data-state={row.getIsSelected() && 'selected'}>
                        {row.getVisibleCells().map(cell => (
                          <TableCell
                            key={cell.id}
                            className="p-2 md:px-4 md:py-3">
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={4}
                        className="p-2 text-xs md:px-4 md:py-3 md:text-sm">
                        No results found
                      </TableCell>
                    </TableRow>
                  )
                ) : null}
              </TableBody>
            </Table>
          </div>
        </div>
        <div className="no-scrollbar flex size-full flex-col gap-6 overflow-visible bg-neutral-700 p-4 text-neutral-0 lg:w-1/2 lg:overflow-auto">
          <div className="flex flex-1 flex-col gap-2 md:gap-4">
            <Card className="flex h-44 w-full flex-1 flex-col overflow-hidden rounded border-none bg-neutral-900 md:h-56 md:rounded-lg">
              <CardHeader className="p-2 pb-1 text-xs md:p-6 md:pb-2 md:text-base">
                <CardTitle>Total AI Trainer Reviews</CardTitle>
              </CardHeader>
              <CardContent className="flex max-h-12 min-h-[200px] flex-1 flex-col p-2 pt-0 md:max-h-full md:p-6 md:pt-0">
                <p className="mb-1 text-base text-azure-400 md:text-2xl">
                  {Math.round(total).toLocaleString('en-US')}
                </p>
                <div className="w-full flex-1">
                  <AreaChart
                    data={graphData}
                    customTooltip={(props: any) => (
                      <CustomTooltip
                        line1={`${Math.round(
                          props?.payload[0]?.value || 0,
                        ).toLocaleString('en-US')} Total Reviews`}
                        {...props}
                      />
                    )}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
          <Contribute />
        </div>
      </div>
    </TightLayout>
  );
}
